import { useEffect, useRef, useState } from 'react'
import clsx from 'clsx'
import Avatar from 'react-avatar'
import { toast } from 'react-toastify'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'

import {
	TApi,
	TRoles,
	TSupporterRoles,
	TSupporterStatus,
	StateOptions,
	TContactModes,
	TContactTimes
} from '../../../../shared/const'
import http from '../../../../shared/http'
import { stateLabel } from '../../../../shared/helpers'
import { optionLabel } from '../../../../shared/optionList'
import { activeAgainLabel, formatDay } from '../../../../shared/unavailability'
import useScrollTopOnTabChange from '../../../../shared/useScrollTopOnTabChange'
import useDialogDismiss from '../../../../shared/useDialogDismiss'
import { askDiscardChanges } from '../../../../shared/confirmDialog'
import { juniorAlumniErrors, juniorAlumniPayload } from '../../../../shared/juniorAlumni'
import { useIsReadOnly } from '../../../../shared/alumni'

import Btn from '../../../../UI/Btn'
import Loading from '../../../../UI/Loading'
import AuthAvatar from '../../../../UI/AuthAvatar'
import JuniorAlumniField from '../../../../UI/JuniorAlumniField'
import UnavailableDialog from '../../../../UI/UnavailableDialog'
import { PlainInput, PlainTextarea, PlainSelect } from '../../../../UI/PlainField'
import Card from './Card'
import EngagementTab from './EngagementTab'
import SupporterLogTab from './SupporterLogTab'

import styles from './editModal.module.scss'

const TABS = [
	{ key: 'data', label: 'personalData' },
	{ key: 'roles', label: 'rolesAndCompetences' },
	{ key: 'target', label: 'targetAudience' },
	{ key: 'engagement', label: 'companiesAndNeeds' },
	{ key: 'status', label: 'status' },
	{ key: 'log', label: 'Comments' }
]

/*
 * Reiter ohne Formularfelder. Dort gibt es nichts zu speichern - eine
 * Schaltflaeche "Speichern" waere dort eine Falle: Sie wuerde die Aenderungen
 * eines anderen Reiters schicken, ohne dass man sie sieht.
 */
const READ_ONLY_TABS = ['engagement', 'status', 'log']

/** Die drei Merkmale des Reiters "Zielgruppe" - auch fuer die Zahl am Reiter. */
const TARGET_FIELDS = ['targetStates', 'targetSchoolTypes', 'targetIndustries']

/*
 * Reihenfolge des Lebenszyklus, wie sie im Status-Reiter erklaert wird.
 * TSupporterStatus ist eine Klasse mit statischen Feldern - deren Reihenfolge
 * ist zwar stabil, aber nicht als Anzeigereihenfolge gedacht. Deshalb hier
 * bewusst ausgeschrieben.
 */
const STATUS_ORDER = [
	TSupporterStatus.INVITED,
	TSupporterStatus.PENDING_APPROVAL,
	TSupporterStatus.ACTIVE,
	TSupporterStatus.UNAVAILABLE,
	TSupporterStatus.ARCHIVED,
	TSupporterStatus.DELETED
]

/** Genau die Felder, die dieses Fenster speichert - auch der Abgleich fuer "geaendert". */
const payloadOf = profile => ({
	firstName: profile.firstName,
	lastName: profile.lastName,
	phone: profile.phone,
	company: profile.company,
	position: profile.position,
	website: profile.website,
	streetName: profile.streetName,
	houseNumber: profile.houseNumber,
	zipCode: profile.zipCode,
	city: profile.city,
	state: profile.state,
	bio: profile.bio,
	linkedIn: profile.linkedIn,
	specializations: profile.specializations,
	contactModes: profile.contactModes || [],
	contactTimes: profile.contactTimes || [],
	contactNote: profile.contactNote,
	openForNewCompanies: profile.openForNewCompanies,
	roles: profile.roles,
	competences: profile.competences,
	targetStates: profile.targetStates,
	targetSchoolTypes: profile.targetSchoolTypes,
	targetIndustries: profile.targetIndustries,
	// Junior Company Teilnahme (Anforderung #181). Nie beantwortet bleibt
	// nie beantwortet: Die Felder gehen erst mit, wenn jemand den Schieber
	// bedient hat — sonst stuende nach jedem Speichern ein „Nein" da.
	...juniorAlumniPayload(profile, { keepUnanswered: true })
})

/**
 * Bearbeiten eines bestehenden Unterstuetzer:innen-Profils durch die
 * Administration.
 *
 * Bis hierher konnten Admins nur einladen, freigeben und loeschen - ein
 * bestehendes Profil liess sich nicht aendern, obwohl die API es konnte.
 * Deckt damit auch "Rollen verwalten" und "Profil archivieren" aus Spec
 * Teil 2 B.1 ab.
 */
export default function EditSupporterModal({ supporterId, lookups, onClose, onSaved, onDelete }) {
	const { t, i18n } = useTranslation()

	// Zielbranchen sind als englische Fassung gespeichert (dieselbe Liste, aus der
	// eine Junior Company ihre Branche wählt) — angezeigt gehört die deutsche.
	const branches = useSelector(state => state.options?.branch) || []

	const [profile, setProfile] = useState(null)
	// Stand beim Laden bzw. nach dem letzten Speichern - daran haengt der
	// Hinweis "nicht gespeicherte Aenderungen" in der Fussleiste.
	const [savedState, setSavedState] = useState(null)
	const [tab, setTab] = useState('data')
	// Alumni (#213): lesen ohne Kommentare (interne Vermerke) und ohne Handlungen.
	const readOnly = useIsReadOnly()
	// Freigeben darf nur die Bundesebene (Anforderung #58) - wie im Menue der Liste.
	const canApprove = useSelector(state => state.user?.role) === TRoles.FEDERAL_ADMIN
	const tabs = readOnly ? TABS.filter(item => item.key !== 'log') : TABS

	const tabsRef = useRef(null)
	useScrollTopOnTabChange(tab, tabsRef)
	const [isSaving, setSaving] = useState(false)
	const [ownCompetence, setOwnCompetence] = useState('')
	// Pausieren fragt vorher nach der Dauer - siehe UI/UnavailableDialog.
	const [isPauseOpen, setPauseOpen] = useState(false)
	// Meldungen an der Karte „Junior Company Teilnahme".
	const [alumniErrors, setAlumniErrors] = useState({})

	useEffect(() => {
		if (!supporterId) return

		setProfile(null)
		setAlumniErrors({})
		http.get(TApi.ADMIN_SUPPORTER_ID(supporterId))
			.then(({ data }) => {
				setProfile(data)
				setSavedState(JSON.stringify(payloadOf(data)))
			})
			.catch(() => {
				// Meldung kommt aus shared/http
				onClose()
			})
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [supporterId])

	const isPending = profile?.availabilityStatus === TSupporterStatus.PENDING_APPROVAL

	const isDirty = Boolean(
		profile && savedState && JSON.stringify(payloadOf(profile)) !== savedState
	)

	// Kreuz, Klick daneben, Esc und "Abbrechen" gehen denselben Weg — mit
	// Rueckfrage, solange ungespeicherte Aenderungen im Fenster stehen.
	const requestClose = async () => {
		if (isDirty && !(await askDiscardChanges())) return
		onClose()
	}

	const backdropProps = useDialogDismiss(requestClose, { enabled: Boolean(supporterId) })

	if (!supporterId) return null

	const patch = changes => setProfile(current => ({ ...current, ...changes }))

	const toggleIn = (field, value) => {
		const current = profile[field] || []
		patch({
			[field]: current.includes(value)
				? current.filter(item => item !== value)
				: [...current, value]
		})
	}

	const save = () => {
		if (!profile.roles?.length) {
			toast.warning(t('atLeastOneRole'))
			return
		}

		// Speichern gilt fuer alle Reiter - die Meldung muss deshalb dorthin
		// fuehren, wo das Feld steht.
		const errors = juniorAlumniErrors(profile)
		setAlumniErrors(errors)
		if (Object.keys(errors).length) {
			setTab('data')
			toast.warning(t('juniorAlumniIncomplete'))
			return
		}

		setSaving(true)
		http.put(TApi.ADMIN_SUPPORTER_ID(supporterId), payloadOf(profile))
			.then(() => {
				setSavedState(JSON.stringify(payloadOf(profile)))
				toast.success(t('Saved successfully'))
				/*
				 * Das Fenster bleibt offen. Es hat sechs Reiter - wer zwischen
				 * ihnen speichert, wollte weiterarbeiten und nicht zurueck in
				 * die Liste. Die Liste dahinter wird trotzdem aufgefrischt.
				 */
				onSaved({ keepOpen: true })
			})
			.catch(() => {}) // Meldung kommt aus shared/http
			.finally(() => setSaving(false))
	}

	/**
	 * Freigeben stand bisher nur im Drei-Punkte-Menue der Liste. Wer ein
	 * angemeldetes Profil erst durchsieht und dann freigibt, musste dafuer
	 * schliessen und in der Liste suchen — die Handlung gehoert hierher.
	 */
	const approve = () => {
		setSaving(true)
		http.post(TApi.ADMIN_SUPPORTER_APPROVE(supporterId))
			.then(() => http.get(TApi.ADMIN_SUPPORTER_ID(supporterId)))
			.then(({ data }) => {
				setProfile(data)
				setSavedState(JSON.stringify(payloadOf(data)))
				toast.success(t('Saved successfully'))
				onSaved({ keepOpen: true })
			})
			.catch(() => {}) // Meldung kommt aus shared/http
			.finally(() => setSaving(false))
	}

	/**
	 * Verfuegbarkeit und Archivierung laufen ueber einen eigenen Endpunkt.
	 *
	 * `unavailableUntil` wird nur beim Pausieren mitgeschickt - der letzte Tag
	 * der Abwesenheit als reiner Tag, oder null fuer "bis auf Weiteres". Danach
	 * schaltet sich das Profil selbst wieder aktiv (naechtlicher Lauf im
	 * Backend), und in beiden Richtungen geht eine E-Mail hinaus.
	 */
	const setStatus = (availabilityStatus, unavailableUntil) => {
		const payload =
			availabilityStatus === TSupporterStatus.UNAVAILABLE
				? { availabilityStatus, unavailableUntil: unavailableUntil || null }
				: { availabilityStatus }

		setSaving(true)
		http.put(TApi.ADMIN_SUPPORTER_STATUS(supporterId), payload)
			.then(({ data }) => {
				setProfile(data)
				setSavedState(JSON.stringify(payloadOf(data)))
				setPauseOpen(false)
				toast.success(t('Saved successfully'))
				onSaved({ keepOpen: true })
			})
			.catch(() => {}) // Meldung kommt aus shared/http
			.finally(() => setSaving(false))
	}

	const catalogCompetences = (lookups?.competenceGroups || []).flatMap(group => group.items)
	const ownCompetences = (profile?.competences || []).filter(
		item => !catalogCompetences.includes(item)
	)

	/*
	 * Zahl am Reiter: zeigt ohne Klick, wie viel dort hinterlegt ist. Vorher
	 * musste man jeden Reiter oeffnen, um zu sehen, ob ueberhaupt etwas
	 * ausgewaehlt war.
	 */
	const tabCount = key => {
		if (!profile) return 0
		if (key === 'roles')
			return (profile.roles?.length || 0) + (profile.competences?.length || 0)
		if (key === 'target')
			return TARGET_FIELDS.reduce((sum, field) => sum + (profile[field]?.length || 0), 0)
		// Am Reiter steht die Zahl der betreuten Junior Companies. Die Antworten
		// auf Bedarfe stehen bewusst nicht darin: Sie kommen ueber eine eigene
		// Abfrage und waeren beim Oeffnen des Fensters noch nicht bekannt - die
		// Zahl spraenge dann nachtraeglich.
		if (key === 'engagement') return profile.connections?.length || 0
		return 0
	}

	/** Eigene, nicht im Katalog stehende Kompetenz ergaenzen. */
	const addOwn = () => {
		const value = ownCompetence.trim()
		if (!value || (profile.competences || []).includes(value)) return
		patch({ competences: [...(profile.competences || []), value] })
		setOwnCompetence('')
	}

	const chips = (field, options, labelFor = value => value) => (
		<div className={styles.chips}>
			{options.map(option => (
				<button
					key={option}
					type="button"
					className={clsx(
						styles.chip,
						(profile[field] || []).includes(option) && styles.chipOn
					)}
					onClick={() => toggleIn(field, option)}
				>
					{labelFor(option)}
				</button>
			))}
		</div>
	)

	/*
	 * labelFor uebersetzt den gespeicherten Wert fuer die Anzeige (z. B.
	 * Bundesland-Enum VIENNA -> "Wien", Anforderung #14). Gespeichert und
	 * verglichen wird weiterhin der Rohwert.
	 */
	const chipCard = (label, field, options, labelFor = value => value) => {
		const selected = profile[field] || []
		const chosen = selected.length
		const fromCatalog = selected.filter(value => options.includes(value)).length
		const allChosen = options.length > 0 && fromCatalog === options.length

		/*
		 * Werte, die nicht (mehr) im Katalog stehen. Ueber die Oberflaeche
		 * entstehen sie nicht - sie kamen ueber die Schnittstelle ins Profil und
		 * blieben dort unsichtbar haengen: kein Chip zeigte sie an, gespeichert
		 * wurden sie trotzdem weiter (Anforderung #29). Hier stehen sie sichtbar
		 * und lassen sich mit einem Klick entfernen; die Schnittstelle nimmt
		 * inzwischen nur noch Katalogwerte an.
		 */
		const stale = selected.filter(value => !options.includes(value))

		return (
			<Card
				key={field}
				title={t(label)}
				hint={chosen ? t('selectedCount', { count: chosen }) : t('noRestriction')}
				action={
					options.length > 0 && (
						<button
							type="button"
							className={styles.cardAction}
							onClick={() => patch({ [field]: allChosen ? [] : [...options] })}
						>
							{t(allChosen ? 'clearSelection' : 'selectAll')}
						</button>
					)
				}
			>
				{chips(field, options, labelFor)}
				{stale.length > 0 && (
					<div className={styles.staleBlock}>
						<p className={styles.hint}>{t('staleTargetValues')}</p>
						<div className={styles.chips}>
							{stale.map(value => (
								<button
									key={value}
									type="button"
									title={t('removeStaleValue')}
									className={clsx(
										styles.chip,
										styles.chipStale,
										styles.chipRemovable
									)}
									onClick={() => toggleIn(field, value)}
								>
									{value}
								</button>
							))}
						</div>
					</div>
				)}
			</Card>
		)
	}

	return (
		<div className={styles.overlay} {...backdropProps}>
			<div className={styles.modal}>
				{!profile ? (
					<Loading />
				) : (
					<>
						<header className={styles.header}>
							<div className={styles.identity}>
								<AuthAvatar
									hashName={profile.avatarHashName}
									className={styles.avatar}
									fallback={
										<Avatar
											name={profile.displayName || '?'}
											size="52"
											round
											color="#c3edef"
											fgColor="#22404d"
										/>
									}
								/>
								<div className={styles.identityText}>
									<h2 className={styles.title}>{profile.displayName}</h2>
									<span className={styles.subtitle}>{profile.email}</span>
									{Boolean(profile.roles?.length) && (
										<span className={styles.roleSummary}>
											{profile.roles
												.map(role => t(`supporterRole${role}`))
												.join(' · ')}
										</span>
									)}
								</div>
							</div>
							{/*
							 * Der Status steht im Kopf und damit in jedem Reiter vor
							 * Augen: ob jemand fuer Schulen sichtbar ist, gehoert zu
							 * jeder Aenderung dazu, nicht nur in den Status-Reiter.
							 */}
							<div className={styles.headerSide}>
								<span
									className={clsx(
										styles.statusBadge,
										styles[`statusBadge${profile.availabilityStatus}`]
									)}
								>
									{t(`supporterStatus${profile.availabilityStatus}`)}
								</span>
								<button type="button" className={styles.close} onClick={requestClose}>
									×
								</button>
							</div>
						</header>

						<div className={styles.tabs} ref={tabsRef}>
							{tabs.map(item => {
								const count = tabCount(item.key)
								return (
									<button
										key={item.key}
										type="button"
										className={clsx(styles.tab, tab === item.key && styles.tabOn)}
										onClick={() => setTab(item.key)}
									>
										{t(item.label)}
										{count > 0 && (
											<span className={styles.tabCount}>{count}</span>
										)}
									</button>
								)
							})}
						</div>

						<div className={styles.body}>
							{/* Alumni (#213): dieselbe Ansicht, aber jedes Feld und jeder
							    Knopf darin gesperrt — ein fieldset sperrt sie alle auf einmal. */}
							<fieldset disabled={readOnly} className={styles.readOnlyFieldset}>
								{tab === 'data' && (
									<div className={styles.cardGrid}>
										<Card title={t('sectionPerson')}>
											<div className={styles.fields}>
												<PlainInput
													label={t('firstName')}
													value={profile.firstName || ''}
													onChange={e => patch({ firstName: e.target.value })}
												/>
												<PlainInput
													label={t('lastName')}
													value={profile.lastName || ''}
													onChange={e => patch({ lastName: e.target.value })}
												/>
												<PlainInput
													label={t('phone')}
													value={profile.phone || ''}
													onChange={e => patch({ phone: e.target.value })}
												/>
												<PlainInput
													label={t('city')}
													value={profile.city || ''}
													onChange={e => patch({ city: e.target.value })}
												/>
												<PlainSelect
													label={t('homeState')}
													placeholder="—"
													options={StateOptions}
													value={profile.state || ''}
													onChange={e =>
														patch({ state: e.target.value || null })
													}
												/>
											</div>
										</Card>

										<Card title={t('sectionCompany')}>
											<div className={styles.fields}>
												<PlainInput
													label={t('company')}
													value={profile.company || ''}
													onChange={e => patch({ company: e.target.value })}
												/>
												<PlainInput
													label={t('position')}
													value={profile.position || ''}
													onChange={e => patch({ position: e.target.value })}
												/>
												<PlainInput
													label="Website"
													placeholder="https://"
													value={profile.website || ''}
													onChange={e => patch({ website: e.target.value })}
												/>
												{/*
												 * LinkedIn wurde schon immer mitgespeichert,
												 * war hier aber nicht zu sehen - eine
												 * Aenderung ueberschrieb den Wert stillschweigend.
												 */}
												<PlainInput
													label="LinkedIn"
													placeholder="https://linkedin.com/in/…"
													value={profile.linkedIn || ''}
													onChange={e => patch({ linkedIn: e.target.value })}
												/>
											</div>
										</Card>

										{/*
										 * Erreichbarkeit: Die Angaben stehen sonst nur im
										 * Selbstprofil - die Administration pflegt sie mit, wenn
										 * eine Person sie am Telefon durchgibt.
										 */}
										<Card
											title={t('contactPreferences')}
											className={styles.cardWide}
										>
											<div className={styles.block}>
												<span className={styles.blockLabel}>{t('contactModes')}</span>
												{chips('contactModes', TContactModes, value =>
													t(`contactMode${value}`)
												)}
											</div>
											<div className={styles.block}>
												<span className={styles.blockLabel}>{t('contactTimes')}</span>
												{chips('contactTimes', TContactTimes, value =>
													t(`contactTime${value}`)
												)}
											</div>
											<PlainTextarea
												label={t('contactNote')}
												rows={2}
												placeholder={t('contactNotePlaceholder')}
												value={profile.contactNote || ''}
												onChange={e => patch({ contactNote: e.target.value })}
											/>
										</Card>

										<Card
											title={t('profileAndAvailability')}
											className={styles.cardWide}
										>
											<PlainTextarea
												label={t('Bio')}
												rows={5}
												value={profile.bio || ''}
												onChange={e => patch({ bio: e.target.value })}
											/>
											<label className={styles.toggleRow}>
												<input
													type="checkbox"
													checked={Boolean(profile.openForNewCompanies)}
													onChange={e =>
														patch({ openForNewCompanies: e.target.checked })
													}
												/>
												<span>
													<span className={styles.toggleLabel}>
														{t('openForNewCompanies')}
													</span>
													<span className={styles.toggleHint}>
														{t('openForNewCompaniesHint')}
													</span>
												</span>
											</label>
										</Card>

										{/*
										 * Anforderung #181: Hat die Person selbst ein Programm
										 * von Junior Oesterreich absolviert? Gefragt in der
										 * Registrierung, gepflegt hier und im eigenen Profil.
										 */}
										<Card title={t('juniorAlumniTitle')} className={styles.cardWide}>
											<JuniorAlumniField
												value={profile}
												errors={alumniErrors}
												onChange={changes => {
													patch(changes)
													setAlumniErrors({})
												}}
											/>
										</Card>
									</div>
								)}

								{tab === 'roles' && (
									<>
										{/*
										 * Rollen als Karten mit einem Satz Erklaerung: Aus
										 * "Jury-Mitglied" allein geht nicht hervor, was das
										 * Setzen bewirkt.
										 */}
										<Card title={t('possibleRoles')} hint={t('rolesHint')}>
											<div className={styles.roleCards}>
												{Object.values(TSupporterRoles).map(role => (
													<button
														key={role}
														type="button"
														className={clsx(
															styles.roleCard,
															(profile.roles || []).includes(role) &&
																styles.roleCardOn
														)}
														onClick={() => toggleIn('roles', role)}
													>
														<span className={styles.roleName}>
															{t(`supporterRole${role}`)}
														</span>
														<span className={styles.roleHint}>
															{t(`supporterRoleHint${role}`)}
														</span>
													</button>
												))}
											</div>
										</Card>

										<div className={styles.cardGrid}>
											{(lookups?.competenceGroups || []).map(group => (
												<Card
													key={group.name}
													title={group.name}
													hint={t('selectedCount', {
														count: group.items.filter(item =>
															(profile.competences || []).includes(item)
														).length
													})}
												>
													{chips('competences', group.items)}
												</Card>
											))}

											<Card
												title={t('furtherCompetences')}
												hint={t('furtherCompetencesHint')}
												className={styles.cardWide}
											>
												{ownCompetences.length ? (
													<div className={styles.chips}>
														{ownCompetences.map(item => (
															<button
																key={item}
																type="button"
																title={t('removeCompetence')}
																className={clsx(
																	styles.chip,
																	styles.chipOn,
																	styles.chipRemovable
																)}
																onClick={() =>
																	toggleIn('competences', item)
																}
															>
																{item}
															</button>
														))}
													</div>
												) : (
													<p className={styles.hint}>{t('noOwnCompetences')}</p>
												)}
												<div className={styles.addRow}>
													<PlainInput
														placeholder={t('addOwnCompetence')}
														value={ownCompetence}
														onChange={e => setOwnCompetence(e.target.value)}
														onKeyDown={e => {
															if (e.key !== 'Enter') return
															// Ohne das schickt Enter im Eingabefeld
															// nichts ab - man muss zur Maus greifen.
															e.preventDefault()
															addOwn()
														}}
													/>
													<Btn type="button" theme="secondary" onClick={addOwn}>
														{t('addCompetence')}
													</Btn>
												</div>
											</Card>
										</div>
									</>
								)}

								{/*
								 * Zielgruppe: nach diesen vier Merkmalen suchen Schulen und
								 * Junior Companies. Frueher hingen sie unten am Reiter
								 * "Rollen & Kompetenzen" - dort waren sie kaum zu finden und
								 * machten den Reiter dreimal so lang wie die uebrigen.
								 */}
								{tab === 'target' && (
									<>
										<p className={styles.tabHint}>{t('targetAudienceHint')}</p>
										<div className={styles.cardGrid}>
											{chipCard(
												'states',
												'targetStates',
												StateOptions.map(option => option.value),
												stateLabel
											)}
											{chipCard(
												'schoolTypes',
												'targetSchoolTypes',
												lookups?.schoolTypes || []
											)}
											{/*
											 * Gespeichert ist die englische Fassung — dieselbe,
											 * die eine Junior Company an ihrer Branche trägt.
											 * Angezeigt gehört die deutsche.
											 */}
											{chipCard(
												'industries',
												'targetIndustries',
												lookups?.industries || [],
												value => optionLabel(branches, value, i18n.language)
											)}
										</div>
									</>
								)}

								{tab === 'engagement' && (
									<EngagementTab
										supporterId={supporterId}
										connections={profile.connections}
									/>
								)}

								{tab === 'log' && !readOnly && <SupporterLogTab supporterId={supporterId} />}

								{tab === 'status' && (
									<>
										<Card
											title={t('currentStatus')}
											hint={t(
												`supporterStatusHint${profile.availabilityStatus}`
											)}
											action={
												<span
													className={clsx(
														styles.statusBadge,
														styles[
															`statusBadge${profile.availabilityStatus}`
														]
													)}
												>
													{t(`supporterStatus${profile.availabilityStatus}`)}
												</span>
											}
										>
											{/*
											 * Bei einer Pause steht hier, bis wann sie
											 * laeuft und wann das Profil von selbst
											 * wieder auftaucht. Ohne diese Zeile ist
											 * "nicht verfuegbar" nicht von "vergessen"
											 * zu unterscheiden.
											 */}
											{profile.availabilityStatus ===
												TSupporterStatus.UNAVAILABLE && (
												<p className={styles.pauseNote}>
													{profile.unavailableUntil
														? t('pauseUntilNote', {
																last: formatDay(
																	profile.unavailableUntil
																),
																back: activeAgainLabel(
																	profile.unavailableUntil
																)
														  })
														: t('pauseOpenNote')}
												</p>
											)}

											{/*
											 * Wartet das Profil auf seine Freigabe, steht
											 * hier, wer sie erteilt (Anforderung #58).
											 * Landesadmins sehen solche Profile in ihrer
											 * Liste, koennen sie aber nicht freigeben —
											 * ohne diese Zeile wirkte das wie ein Fehler.
											 */}
											{profile.availabilityStatus ===
												TSupporterStatus.PENDING_APPROVAL && (
												<p className={styles.pauseNote}>
													{t('approveSupporterFederalOnly')}
												</p>
											)}

											{/*
											 * Von einer Junior Company selbst eingeladen.
											 * Ohne diese Zeile wäre nicht zu erklären,
											 * warum ein aktives Profil in der Suche der
											 * übrigen Junior Companies fehlt.
											 */}
											{Boolean(profile.invitedByCompany) && (
												<p className={styles.pauseNote}>
													{profile.visibleToAllCompanies
														? t('invitedByCompanyOpen', {
																company: profile.invitedByCompany.name
														  })
														: t('invitedByCompanyOnly', {
																company: profile.invitedByCompany.name
														  })}
												</p>
											)}

											{/*
											 * Eine wartende Selbstregistrierung verlaesst
											 * diesen Status nur ueber "Freigeben" oder
											 * "Profil loeschen". "Aktiv setzen" stand hier
											 * daneben und tat scheinbar dasselbe - nur ohne
											 * Freigabe-Mail, ohne Vermerk, wer freigegeben
											 * hat, und auch fuer Landesadmins. Der Server
											 * weist den Statuswechsel inzwischen ab.
											 */}
											<div className={clsx(styles.statusActions, readOnly && styles.hidden)}>
												{isPending && canApprove && (
													<Btn theme="success" disabled={isSaving} onClick={approve}>
														{t('approveSupporter')}
													</Btn>
												)}
												{!isPending &&
													profile.availabilityStatus !==
													TSupporterStatus.ACTIVE && (
													<Btn
														theme="success"
														disabled={isSaving}
														onClick={() =>
															setStatus(TSupporterStatus.ACTIVE)
														}
													>
														{t('setActive')}
													</Btn>
												)}
												{!isPending &&
													profile.availabilityStatus !==
													TSupporterStatus.UNAVAILABLE && (
													<Btn
														theme="secondary"
														disabled={isSaving}
														onClick={() => setPauseOpen(true)}
													>
														{t('setUnavailable')}
													</Btn>
												)}
												{!isPending &&
													profile.availabilityStatus !==
													TSupporterStatus.ARCHIVED && (
													<Btn
														theme="secondary"
														disabled={isSaving}
														onClick={() =>
															setStatus(TSupporterStatus.ARCHIVED)
														}
													>
														{t('archiveProfile')}
													</Btn>
												)}
												{onDelete && (
													<Btn
														theme="secondary"
														className={styles.dangerAction}
														disabled={isSaving}
														onClick={() => onDelete(profile)}
													>
														{t('deleteProfile')}
													</Btn>
												)}
											</div>
										</Card>

										{/*
										 * Legende aller Status. Ohne sie war nicht erkennbar,
										 * was die Schaltflaechen bewirken und warum ein Profil
										 * fuer Schulen unsichtbar ist.
										 */}
										<Card title={t('statusExplained')}>
											<dl className={styles.legendList}>
												{STATUS_ORDER.map(status => (
													<div
														key={status}
														className={clsx(
															styles.legendRow,
															status === profile.availabilityStatus &&
																styles.legendRowOn
														)}
													>
														<dt className={styles.legendTerm}>
															{t(`supporterStatus${status}`)}
															{status === profile.availabilityStatus && (
																<span className={styles.legendCurrent}>
																	{t('currentlySet')}
																</span>
															)}
														</dt>
														<dd className={styles.legendText}>
															{t(`supporterStatusHint${status}`)}
														</dd>
													</div>
												))}
											</dl>
										</Card>
									</>
								)}
							</fieldset>
						</div>

						<footer className={styles.footer}>
							{/*
							 * Die Reiter tragen ihre Aenderungen gemeinsam - wer den
							 * Reiter wechselt, sieht sonst nicht, dass noch etwas
							 * offen ist.
							 */}
							<span className={styles.footerNote}>
								{isDirty && !READ_ONLY_TABS.includes(tab) && (
									<>
										<span className={styles.footerDot} />
										{t('unsavedChanges')}
									</>
								)}
							</span>
							<Btn type="button" theme="secondary" onClick={requestClose}>
								{readOnly ? t('Close') : t('Cancel')}
							</Btn>
							{!READ_ONLY_TABS.includes(tab) && !readOnly && (
								/*
								 * Orange und leise pulsierend, solange etwas offen
								 * ist: Das Fenster bleibt nach dem Speichern stehen,
								 * also muss man ihm ansehen koennen, ob noch etwas
								 * darauf wartet.
								 */
								<Btn disabled={isSaving} attention={isDirty} onClick={save}>
									{t('save')}
								</Btn>
							)}
						</footer>
					</>
				)}
			</div>

			{isPauseOpen && (
				<UnavailableDialog
					byAdmin
					name={profile?.displayName}
					isSaving={isSaving}
					onCancel={() => setPauseOpen(false)}
					onConfirm={until => setStatus(TSupporterStatus.UNAVAILABLE, until)}
				/>
			)}
		</div>
	)
}
